import type {
  ApplicationState,
  PaymentEnvironment,
  PaymentInfo,
  PaymentProvider,
  PaymentState,
  ProviderDefinition,
} from '@concours/shared'

export const PAYMENTS_KEY = ['admin', 'payments'] as const
export const METHODS_KEY = ['admin', 'payment-methods'] as const

export interface AdminPaymentRow extends PaymentInfo {
  anomaly: string | null
  anomalyLabel: string | null
  reconciledAt: string | null
  applicationId: string
  applicationNumber: string
  competitionReference: string
  candidate: string
}

export interface PaymentEvent {
  id: number
  source: 'webhook' | 'poll' | 'manual' | 'reconcile'
  type: string | null
  authentic: boolean
  outcome: string | null
  detail: string | null
  receivedAt: string | null
  payload: unknown
}

export interface AdminPaymentDetail extends PaymentInfo {
  anomaly: string | null
  anomalyLabel: string | null
  environment: PaymentEnvironment | null
  providerSessionId: string | null
  manualReason: string | null
  refundedAt: string | null
  refundReason: string | null
  refundedBy: string | null
  createdBy: string | null
  reconciledAt: string | null
  lastCheckedAt: string | null
  refundable: boolean
  refundViaProvider: boolean
  application: { id: string; number: string; status: ApplicationState }
  competition: { id: string; reference: string; title: string }
  candidate: { name: string; email: string }
  events: PaymentEvent[]
}

export interface PaymentMethod {
  id: string
  code: string
  provider: PaymentProvider
  providerLabel: string
  label: string
  description: string | null
  environment: PaymentEnvironment
  environmentLabel: string
  config: Record<string, string | null>
  secrets: Record<string, { set: boolean; hint?: string }>
  missing: string[]
  fees: { fixed: number; percent: number }
  currency: string
  sortOrder: number
  isActive: boolean
  refundable: boolean
  webhookUrl: string
  lastTest: { at: string | null; ok: boolean; message: string | null } | null
  openPayments?: number
}

export interface MethodsResponse {
  data: PaymentMethod[]
  providers: Record<PaymentProvider, ProviderDefinition>
  environments: Record<PaymentEnvironment, string>
}

export const EVENT_OUTCOME_VARIANT: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> = {
  confirmed: 'success',
  failed: 'warning',
  ignored: 'neutral',
  duplicate: 'neutral',
  rejected: 'danger',
  anomaly: 'danger',
  error: 'danger',
}

export type StatusCounts = Partial<Record<PaymentState, { count: number; amount: number }>>
